import type { ReactNode } from 'react';
import {
  baseOf,
  columnPath,
  gridTicks,
  midOf,
  spanEdges,
  xOf,
  yOf,
  type Frame,
} from './columns-geometry';

/** Round hairlines with their values. On a phone the values sit inside the plot, over the lines. */
export function Grid({ f, format }: { f: Frame; format: (n: number) => string }): ReactNode {
  return (
    <>
      {gridTicks(f).map((t) => {
        const y = yOf(f, t);
        return (
          <g key={t}>
            <line className="pui-chart__grid" x1={f.left} x2={f.width - 4} y1={y} y2={y} />
            <text
              className={f.narrow ? 'pui-chart__tick--inside' : undefined}
              x={f.narrow ? f.left : f.left - 8}
              y={f.narrow ? y - 4 : y + 4}
              textAnchor={f.narrow ? 'start' : 'end'}
            >
              {format(t)}
            </text>
          </g>
        );
      })}
    </>
  );
}

/** The diagonal fill of a bucket still filling. */
export function Hatch({ id }: { id: string }): ReactNode {
  return (
    <defs>
      <pattern
        id={id}
        width="6"
        height="6"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect className="pui-chart__hatch-bg" width="6" height="6" />
        <line className="pui-chart__hatch" x1="0" y1="0" x2="0" y2="6" />
      </pattern>
    </defs>
  );
}

export interface BarState {
  on: boolean;
  dim: boolean;
  picked: boolean;
}

/**
 * One column. A partial bucket's hatch is set as a style, because CSS beats an SVG presentation
 * attribute and the series colour used to paint over it (round 1 found the "so far" month solid).
 */
export function Bar(props: {
  value: number;
  partial: boolean;
  i: number;
  f: Frame;
  hatch: string;
  state: BarState;
}) {
  const { value, partial, i, f, hatch, state } = props;
  const y = yOf(f, value);
  const h = baseOf(f) - y;
  if (h <= 0) return null;
  const cls = [
    'pui-chart__col',
    partial && 'pui-chart__col--partial',
    state.on && 'is-active',
    state.dim && 'is-dim',
    state.picked && 'is-selected',
  ];
  return (
    <path
      className={cls.filter(Boolean).join(' ')}
      d={columnPath(xOf(f, i), y, f.barW, h)}
      style={partial ? { fill: `url(#${hatch})` } : undefined}
    />
  );
}

/**
 * A bucket's projected end as a hollow outline above its column (round 3, dataviz M1): the open
 * top of what the pace says is still to come. Nothing when the projection is not above the value.
 */
export function Projection(props: { from: number; to: number; i: number; f: Frame }): ReactNode {
  const { from, to, i, f } = props;
  const y = yOf(f, to);
  const h = yOf(f, from) - y;
  if (h <= 1) return null;
  return (
    <path
      className="pui-chart__projected"
      d={columnPath(xOf(f, i) + 0.5, y + 0.5, f.barW - 1, h)}
    />
  );
}

/** Points for a polyline through the defined values; a gap (no history) is left undrawn. */
function pointsOf(values: readonly (number | undefined)[], f: Frame): string[] {
  return values
    .map((v, i) => (v === undefined ? null : `${midOf(f, i)},${yOf(f, v)}`))
    .filter((p): p is string => p !== null);
}

/**
 * The previous period as a quiet line over the columns (round 2, dataviz N1): two bar layers at a 7px
 * pitch cannot be read, a line next to this period's average can. Nothing where there is no history.
 */
export function GhostLine({
  values,
  f,
}: {
  values: readonly (number | undefined)[];
  f: Frame;
}): ReactNode {
  const points = pointsOf(values, f);
  return points.length > 1 ? (
    <polyline className="pui-chart__ghost-line" points={points.join(' ')} />
  ) : null;
}

/** A line on the same axis and unit (a 7-day average), with a surface halo so it separates from the bars. */
export function Line({
  values,
  f,
}: {
  values: readonly (number | undefined)[];
  f: Frame;
}): ReactNode {
  const points = pointsOf(values, f);
  return points.length > 1 ? (
    <polyline className="pui-chart__line pui-chart__line--over" points={points.join(' ')} />
  ) : null;
}

/**
 * Where the reference's label sits: at the right end on a desk; on a phone at the left end above the
 * line (round 2, dataviz S4), stepped past the grid's own value when one sits at the same height.
 */
function referenceLabelX(f: Frame, y: number): { x: number; anchor: 'start' | 'end' } {
  if (!f.narrow) return { x: f.width - 6, anchor: 'end' };
  const crowded = gridTicks(f).some((t) => Math.abs(yOf(f, t) - y) < 14);
  return { x: f.left + (crowded ? 34 : 2), anchor: 'start' };
}

/** A labelled horizontal reference in the chart's own unit: "Pro includes 10,000 credits". */
export function Reference({
  value,
  label,
  f,
}: {
  value: number;
  label: string;
  f: Frame;
}): ReactNode {
  const y = yOf(f, value);
  const at = referenceLabelX(f, y);
  return (
    <g className="pui-chart__reference">
      <line x1={f.left} x2={f.width - 4} y1={y} y2={y} />
      <text x={at.x} y={y - 5} textAnchor={at.anchor}>
        {label}
      </text>
    </g>
  );
}

/** The hairline from the active column down to the axis: solid, as the dataviz rules ask. */
export function Crosshair({ f, i }: { f: Frame; i: number }): ReactNode {
  const x = midOf(f, i);
  return <line className="pui-chart__crosshair" x1={x} x2={x} y1={yOf(f, f.top)} y2={baseOf(f)} />;
}

/** The span being dragged or chosen, as a quiet band the full plot height behind the columns. */
export function SpanBand({ f, from, to }: { f: Frame; from: number; to: number }): ReactNode {
  const [x1, x2] = spanEdges(f, from, to);
  return (
    <rect
      className="pui-chart__span"
      x={x1}
      y={yOf(f, f.top)}
      width={x2 - x1}
      height={baseOf(f) - yOf(f, f.top)}
    />
  );
}

/** The grip a span's end is dragged by: a hairline the plot's height with a pill at its middle. */
function Handle({ f, x }: { f: Frame; x: number }): ReactNode {
  const top = yOf(f, f.top);
  const mid = (top + baseOf(f)) / 2;
  return (
    <g className="pui-chart__handle">
      <line x1={x} x2={x} y1={top} y2={baseOf(f)} />
      <rect className="pui-chart__grip" x={x - 3} y={mid - 9} width={6} height={18} rx={3} />
      <rect className="pui-chart__grab" x={x - 8} y={top} width={16} height={baseOf(f) - top} />
    </g>
  );
}

/** Handles at both ends of a chosen span: drag one to stretch or shrink the span (round 2, dataviz S1). */
export function SpanHandles({ f, from, to }: { f: Frame; from: number; to: number }): ReactNode {
  const [x1, x2] = spanEdges(f, from, to);
  return (
    <>
      <Handle f={f} x={x1} />
      <Handle f={f} x={x2} />
    </>
  );
}
